/* Tobfun specifications v1. Read-only: never intercepts cart or checkout requests. */
(() => {
  'use strict';
  if (customElements.get('tobfun-specs')) return;
  const configRequests = new Map();
  const catalogCache = new WeakMap();
  const safeJSON = (node, fallback = []) => {
    try { return JSON.parse(node?.textContent || ''); } catch { return fallback; }
  };
  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const imageURL = (value) => {
    if (!value) return null;
    try {
      const url = new URL(value, location.href);
      return ['https:', 'http:'].includes(url.protocol) ? url.href : null;
    } catch { return null; }
  };
  const optionLabel = (variant, profile) => {
    if (variant.title === 'Default Title' || /\bPRO X\b/i.test(profile?.name || '')) return '';
    const option = variant.title.split(' / ').pop();
    return /^(white|green|yellow)$/i.test(option) ? option[0].toUpperCase() + option.slice(1).toLowerCase() : option;
  };
  let dialog, owner, launcher, scrollSnapshot, snapshot, pointerStartedOutside = false;
  function makeDialog() {
    if (dialog) return;
    dialog = el('dialog', 'tf-specs-dialog');
    dialog.id = 'TobfunSpecificationsDialog';
    dialog.setAttribute('aria-labelledby', 'TobfunSpecificationsTitle');
    dialog.setAttribute('aria-modal', 'true');
    // Static markup only. All product and configuration text is inserted with textContent.
    dialog.innerHTML = '<header class="tf-specs-head"><p class="tf-specs-eyebrow">TOBFUN · THE CONSTRUCTION CREW</p><h2 id="TobfunSpecificationsTitle">Product Specifications</h2><p class="tf-specs-subtitle" aria-live="polite"></p><button type="button" class="tf-specs-close" aria-label="Close product specifications">×</button></header><div class="tf-specs-scopes" role="group" aria-label="Specifications for"><button type="button" data-scope="bundle">Selected set below</button><button type="button" data-scope="main">This vehicle</button></div><div class="tf-specs-scroll" tabindex="0" role="region" aria-label="Vehicle specifications"></div>';
    document.body.append(dialog);
    dialog.querySelector('.tf-specs-close').addEventListener('click', () => dialog.close());
    const outside = (event) => {
      const rect = dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    };
    dialog.addEventListener('pointerdown', (event) => { pointerStartedOutside = event.target === dialog && outside(event); });
    dialog.addEventListener('click', (event) => {
      if (pointerStartedOutside && event.target === dialog && outside(event)) dialog.close();
      pointerStartedOutside = false;
    });
    dialog.querySelectorAll('[data-scope]').forEach(button => button.addEventListener('click', () => {
      if (!owner) return;
      owner.mode = button.dataset.scope;
      snapshot = null;
      render();
      dialog.querySelector('.tf-specs-scroll').scrollTop = 0;
    }));
    // Native modal dialogs contain keyboard focus. This also keeps Tab within our
    // dialog if a theme focus handler tries to move it into a background overlay.
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { event.stopPropagation(); return; }
      if (event.key !== 'Tab') return;
      const stops = [...dialog.querySelectorAll('button:not([disabled]),[tabindex="0"]')].filter(node => node.getClientRects().length);
      if (!stops.length) return;
      const first = stops[0], last = stops[stops.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
    dialog.addEventListener('close', () => {
      unlockScroll();
      launcher?.setAttribute('aria-expanded', 'false');
      const restore = launcher;
      const previousOwner = owner;
      owner = null; snapshot = null; launcher = null;
      if (previousOwner?.isConnected) { previousOwner.mode = undefined; previousOwner.summary(); }
      if (restore?.isConnected) restore.focus({ preventScroll: true });
    });
  }
  function lockScroll() {
    const body = document.body;
    const values = Object.fromEntries(['position', 'top', 'left', 'right', 'width', 'overflow'].map(key => [key, [body.style.getPropertyValue(key), body.style.getPropertyPriority(key)]]));
    scrollSnapshot = { values, y: window.scrollY, html: [document.documentElement.style.getPropertyValue('overflow'), document.documentElement.style.getPropertyPriority('overflow')] };
    Object.entries({ position: 'fixed', top: `-${scrollSnapshot.y}px`, left: '0', right: '0', width: '100%', overflow: 'hidden' }).forEach(([key, val]) => body.style.setProperty(key, val));
    document.documentElement.style.setProperty('overflow', 'hidden');
  }
  function unlockScroll() {
    if (!scrollSnapshot) return;
    const saved = scrollSnapshot;
    scrollSnapshot = null;
    for (const [key, [value, priority]] of Object.entries(saved.values)) {
      if (value) document.body.style.setProperty(key, value, priority);
      else document.body.style.removeProperty(key);
    }
    if (saved.html[0]) document.documentElement.style.setProperty('overflow', ...saved.html);
    else document.documentElement.style.removeProperty('overflow');
    // Prevent theme smooth scrolling from animating the restoration on iOS.
    const html = document.documentElement;
    const behavior = [html.style.getPropertyValue('scroll-behavior'), html.style.getPropertyPriority('scroll-behavior')];
    html.style.setProperty('scroll-behavior', 'auto', 'important');
    window.scrollTo(0, saved.y);
    if (behavior[0]) html.style.setProperty('scroll-behavior', ...behavior);
    else html.style.removeProperty('scroll-behavior');
  }
  function render() {
    if (!owner || !dialog?.open) return;
    const state = owner.readState();
    const fingerprint = JSON.stringify(state);
    if (snapshot === fingerprint) return;
    snapshot = fingerprint;
    const content = dialog.querySelector('.tf-specs-scroll');
    const fragment = document.createDocumentFragment();
    const fixedBundleMode = state.mode === 'fixed-bundle';
    const bundleMode = state.mode === 'bundle' || fixedBundleMode;
    const validRows = state.rows.filter(row => row.variant);
    const containsProExcavator = validRows.some(row => owner.profile(row.variant)?.name === 'RC Excavator PRO X');
    dialog.querySelector('.tf-specs-subtitle').textContent = fixedBundleMode
      ? `${state.rows.length} vehicles included in this ${state.rows.length}-pack`
      : bundleMode
        ? `${state.rows.length} selected vehicle${state.rows.length === 1 ? '' : 's'} · follows the set builder below`
        : 'The vehicle selected beside the main Add to cart button';
    const scopes = dialog.querySelector('.tf-specs-scopes');
    scopes.hidden = !owner.bundleRoot() || !owner.mainRoot();
    scopes.querySelectorAll('[data-scope]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.scope === (fixedBundleMode ? 'main' : state.mode))));
    if (!state.rows.length) fragment.append(el('p', 'tf-specs-empty', bundleMode ? 'Select vehicles in the set builder to see their specifications together.' : 'Choose an available vehicle option to see its specifications.'));
    else {
      const included = el('section', 'tf-specs-included');
      included.append(el('h3', '', fixedBundleMode ? `Exactly What's Included in Your ${state.rows.length}-Pack` : bundleMode ? "Exactly What's Included in Your Set" : "What's Included with This Vehicle"));
      const list = el('ul');
      state.rows.forEach(row => {
        const profile = row.variant && owner.profile(row.variant);
        const option = row.variant && optionLabel(row.variant, profile);
        list.append(el('li', '', row.variant ? `1 × ${profile?.name || row.variant.productTitle}${option ? ' — ' + option : ''}` : 'Selected vehicle — choose a valid option to view details'));
      });
      included.append(list);
      if (state.total && bundleMode && validRows.length === state.rows.length) {
        const price = el('p', 'tf-specs-total', 'Selected set total');
        price.append(el('strong', '', state.total));
        included.append(price);
      }
      included.append(el('p', 'tf-specs-note', containsProExcavator
        ? 'The RC Excavator PRO X includes 2 FREE powered attachments: a hydraulic breaker and a grapple / grabber. Full vehicle accessories are listed below.'
        : 'Vehicle accessories are listed under each model below.'));
      fragment.append(included);
    }
    validRows.forEach((row, index) => {
      const v = row.variant, profile = owner.profile(v);
      const card = el('article', 'tf-specs-card');
      card.dataset.variantId = v.id;
      const intro = el('div', 'tf-specs-card__intro');
      const url = imageURL(owner.config?.imageOverrides?.[String(v.id)] || v.image);
      if (url) {
        const img = el('img', 'tf-specs-photo');
        img.src = url; img.alt = `${profile?.name || v.productTitle} — ${v.title}`;
        img.width = 148; img.height = 148; img.loading = 'lazy'; img.decoding = 'async';
        intro.append(img);
      } else intro.append(el('div', 'tf-specs-photo', ''));
      const details = el('div');
      details.append(el('p', 'tf-specs-number', `VEHICLE ${String(index + 1).padStart(2, '0')}`));
      details.append(el('h3', '', profile?.name || v.productTitle));
      if (profile?.highlights?.length) {
        const highlights = el('ul', 'tf-specs-highlights');
        profile.highlights.forEach(text => highlights.append(el('li', '', text)));
        details.append(highlights);
      }
      const option = optionLabel(v, profile);
      if (option) details.append(el('p', 'tf-specs-option', `Selected: ${option}`));
      if (v.available === false) details.append(el('p', 'tf-specs-option', 'Currently unavailable'));
      intro.append(details); card.append(intro);
      const dl = el('dl');
      const specs = [...(profile?.specs || []), ...(owner.config?.sharedSpecs?.[profile?.sharedSpecs] || [])].filter(([, value]) => value !== null && value !== '');
      if (!bundleMode && v.priceDisplay) specs.push(['Current vehicle price', v.priceDisplay]);
      if (v.sku) specs.push(['SKU', v.sku]);
      specs.forEach(([label, value]) => {
        const line = el('div', 'tf-specs-row');
        line.append(el('dt', '', label), el('dd', '', value)); dl.append(line);
      });
      card.append(dl);
      const pack = el('div', 'tf-specs-package');
      pack.append(el('h4', '', 'Vehicle accessories'));
      if (profile?.included?.length) {
        const items = el('ul');
        profile.included.forEach(text => items.append(el('li', '', text))); pack.append(items);
      } else pack.append(el('p', 'tf-specs-note', 'Please contact us for the accessory list for this exact model.'));
      (profile?.notes || []).forEach(text => pack.append(el('p', 'tf-specs-note', text)));
      if (!profile) pack.append(el('p', 'tf-specs-note', 'Detailed specifications for this option are being updated.'));
      card.append(pack); fragment.append(card);
    });
    if (state.rows.length) fragment.append(el('p', 'tf-specs-footnote', 'Use on dry surfaces. Keep the vehicle away from water and deep mud. Charge only with the supplied cable under adult supervision. Features apply to the exact selected model.'));
    content.replaceChildren(fragment);
  }
  class TobfunSpecs extends HTMLElement {
    connectedCallback() {
      this.cleanup?.();
      const abort = new AbortController();
      this.boundBundle = null;
      this.cleanup = () => { abort.abort(); this.observer?.disconnect(); cancelAnimationFrame(this.frame); cancelAnimationFrame(this.nextFrame); };
      const catalogNode = this.querySelector('[data-tf-specs-catalog]') || document.querySelector('[data-tf-specs-catalog-root]');
      if (catalogNode && !catalogCache.has(catalogNode)) catalogCache.set(catalogNode, safeJSON(catalogNode));
      this.catalog = catalogCache.get(catalogNode) || [];
      this.button = this.querySelector('[data-tf-specs-open]');
      if (!this.button || typeof HTMLDialogElement === 'undefined' || !HTMLDialogElement.prototype.showModal) return;
      this.button.hidden = false;
      this.button.setAttribute('aria-controls', 'TobfunSpecificationsDialog');
      this.button.addEventListener('click', () => this.open(), { signal: abort.signal });
      const schedule = () => {
        cancelAnimationFrame(this.frame); cancelAnimationFrame(this.nextFrame);
        this.frame = requestAnimationFrame(() => { this.nextFrame = requestAnimationFrame(() => { this.summary(); if (owner === this) render(); }); });
      };
      document.addEventListener('change', (event) => {
        if (this.mainRoot()?.contains(event.target) || this.bundleRoot()?.contains(event.target)) schedule();
      }, { signal: abort.signal });
      document.addEventListener('shopify:section:load', schedule, { signal: abort.signal });
      document.addEventListener('shopify:section:unload', (event) => {
        if (owner === this && (event.target.contains(this) || event.target.contains(this.bundleRoot()))) dialog?.close();
      }, { signal: abort.signal });
      // The existing custom event is non-bubbling, so attach directly to its section.
      this.bindBundle = () => {
        const bundle = this.bundleRoot();
        if (bundle && bundle !== this.boundBundle) {
          this.boundBundle = bundle;
          bundle.addEventListener('tobfun:bundle-variant-updated', schedule, { signal: abort.signal });
        }
        this.observer?.disconnect();
        this.observer = new MutationObserver(schedule);
        if (bundle) {
          bundle.querySelectorAll('.bundle-deals__checkbox-js').forEach(checkbox => this.observer.observe(checkbox, { attributes: true, attributeFilter: ['data-id', 'data-price', 'checked'] }));
          const total = bundle.querySelector('.bundle-deals__total-price-js');
          if (total) this.observer.observe(total, { subtree: true, childList: true, characterData: true });
        }
        const main = this.mainRoot();
        main?.querySelectorAll('form[action*="/cart/add"] [name="id"]').forEach(input => this.observer.observe(input, { attributes: true, attributeFilter: ['value'] }));
      };
      document.addEventListener('shopify:section:load', () => { this.bindBundle(); schedule(); }, { signal: abort.signal });
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { this.bindBundle(); schedule(); }, { once: true, signal: abort.signal });
      else { this.bindBundle(); schedule(); }
    }
    disconnectedCallback() {
      if (owner === this) dialog?.close();
      this.cleanup?.();
    }
    mainRoot() {
      return document.getElementById(this.dataset.mainSection) || document.querySelector('[id^="MainProduct-"]');
    }
    bundleRoot() {
      return document.getElementById(this.dataset.bundleSection) || document.querySelector('[id^="TobfunPremiumBundle-"]');
    }
    profile(variant) {
      let key = variant.profileKey || this.config?.variantProfiles?.[String(variant.id)];
      if (!key) {
        const selected = [variant.options?.[0], variant.title, variant.sku].filter(Boolean).join(' ').toLowerCase();
        if (/construction\s*[34]\s*-\s*pack/.test(selected)) return null;
        const type = selected + ' ' + (variant.productTitle || '').toLowerCase();
        const pro = /pro\s*x/.test(selected);
        if (/dump\s*truck/.test(selected)) key = pro ? 'truck-pro' : 'truck-standard';
        else if (/loader/.test(selected)) key = pro ? 'loader-pro' : 'loader-standard';
        else if (/bulldozer/.test(selected)) key = 'bulldozer-standard';
        else if (/excavator/.test(type)) key = pro ? 'excavator-pro' : 'excavator-standard';
        else if (/dump\s*truck/.test(type)) key = pro ? 'truck-pro' : 'truck-standard';
        else if (/loader/.test(type)) key = pro ? 'loader-pro' : 'loader-standard';
        else if (/bulldozer/.test(type)) key = 'bulldozer-standard';
      }
      return this.config?.profiles?.[key] || null;
    }
    readState() {
      const mode = this.mode || (this.dataset.context === 'main' ? 'main' : this.dataset.context === 'bundle' || this.bundleRoot() ? 'bundle' : 'main');
      const rows = [];
      if (mode === 'bundle') {
        const bundle = this.bundleRoot();
        bundle?.querySelectorAll('.bundle-deals__product-js').forEach(card => {
          const checkbox = card.querySelector('.bundle-deals__checkbox-js');
          if (!checkbox?.checked) return;
          const options = [...card.querySelectorAll('.bundle-deals__variant-selects-js select')].map(select => select.value);
          const list = [...card.querySelectorAll('script[type="application/json"]')].map(script => safeJSON(script, null)).find(Array.isArray);
          let id = checkbox.dataset.id;
          // Resolve actual options first: existing theme updates data-id on a later frame.
          if (options.length && list?.length) id = list.find(v => v.options?.every((value, index) => value === options[index]))?.id;
          const variant = this.catalog.find(v => String(v.id) === String(id) && String(v.productId) === String(card.dataset.productId));
          rows.push({ variant: variant || null });
        });
        return { mode, rows, total: bundle?.querySelector('.bundle-deals__total-price-js')?.textContent.trim() || '' };
      }
      const main = this.mainRoot();
      const picker = main?.querySelector('variant-selects');
      let id;
      if (picker?.currentVariant) id = picker.currentVariant.id;
      else {
        const controls = picker ? [...picker.querySelectorAll('select,input[type="radio"]:checked')] : [];
        const options = controls.map(control => control.value);
        if (options.length) id = this.catalog.find(v => String(v.productId) === String(main?.querySelector('input[name="product-id"]')?.value) && v.title.split(' / ').every((value, index) => value === options[index]))?.id;
        else id = main?.querySelector('form[action*="/cart/add"] [name="id"]')?.value;
      }
      const variant = this.catalog.find(v => String(v.id) === String(id));
      const bundleText = variant && [variant.title, variant.sku, ...(variant.options || [])].filter(Boolean).join(' ').toLowerCase();
      const fixedBundle = bundleText && this.config?.fixedBundles?.find(bundle =>
        bundleText.includes(bundle.optionContains.toLowerCase()));
      if (fixedBundle) {
        const members = fixedBundle.components.map((component, index) => this.catalog.find(item =>
          this.profile(item) === this.config.profiles[component.profile] &&
          (!component.optionContains || item.title.toLowerCase().includes(component.optionContains.toLowerCase()))) || {
          id: `bundle-${component.profile}-${index}`,
          profileKey: component.profile,
          productTitle: this.config.profiles[component.profile]?.name || 'RC construction vehicle',
          title: component.optionContains || 'Default Title',
          available: true
        });
        return { mode: 'fixed-bundle', rows: members.map(member => ({ variant: member })), total: '' };
      }
      if (variant) rows.push({ variant });
      return { mode, rows, total: '' };
    }
    summary() {
      const state = this.readState();
      const label = this.querySelector('[data-tf-specs-summary]');
      const selected = state.rows[0]?.variant;
      const bundleText = selected && [selected.title, selected.sku, ...(selected.options || [])].filter(Boolean).join(' ');
      const bundleSize = state.mode === 'fixed-bundle'
        ? state.rows.length
        : Number(bundleText?.match(/construction\s*([34])\s*-\s*pack/i)?.[1] || 0);
      const text = bundleSize
        ? `${bundleSize} vehicles included · view all specifications`
        : state.mode === 'bundle'
          ? `Selected set below · ${state.rows.length} vehicle${state.rows.length === 1 ? '' : 's'} · view all at once`
          : 'View details for this vehicle';
      if (label && label.textContent !== text) label.textContent = text;
    }
    async open() {
      if (dialog?.open) return;
      // Do not put another native modal in the background or modify theme popups.
      if (document.querySelector('dialog[open]')) return;
      this.button.classList.remove('tf-specs-trigger--error');
      this.button.disabled = true;
      try {
        if (!configRequests.has(this.dataset.configUrl)) {
          configRequests.set(this.dataset.configUrl, fetch(this.dataset.configUrl).then(response => {
            if (!response.ok) throw new Error('Specifications unavailable');
            return response.json();
          }).catch(error => { configRequests.delete(this.dataset.configUrl); throw error; }));
        }
        this.config = await configRequests.get(this.dataset.configUrl);
        if (!this.isConnected || dialog?.open) return;
        makeDialog(); owner = this; launcher = this.button;
        this.mode = this.dataset.context === 'main' ? 'main' : this.dataset.context === 'bundle' || this.bundleRoot() ? 'bundle' : 'main';
        snapshot = null;
        lockScroll();
        dialog.showModal();
        this.button.setAttribute('aria-expanded', 'true');
        render();
        dialog.querySelector('.tf-specs-scroll').scrollTop = 0;
        dialog.querySelector('.tf-specs-close').focus({ preventScroll: true });
      } catch (error) {
        if (owner === this) { if (dialog?.open) dialog.close(); else { unlockScroll(); owner = null; } }
        const label = this.querySelector('[data-tf-specs-summary]');
        if (label) label.textContent = 'Specifications could not load. Please try again.';
        this.button.classList.add('tf-specs-trigger--error');
        console.warn('[Tobfun specifications]', error);
      } finally { this.button.disabled = false; }
    }
  }
  customElements.define('tobfun-specs', TobfunSpecs);
})();
